import type { ReactNode } from "react";
import { CSS } from "@dnd-kit/utilities";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { GripVertical } from "lucide-react";

export type QuotePlacementDropData = {
  kind: "stage" | "subgroup" | "before" | "after";
  stage: string;
  subGroup?: string | null;
  targetIndex?: number;
};

export function DraggableQuoteRow({
  itemKey,
  index,
  stage,
  subGroup,
  children,
}: {
  itemKey: string;
  index: number;
  stage: string;
  subGroup?: string | null;
  children: ReactNode;
}) {
  const { attributes, listeners, setNodeRef: setDraggableRef, transform, isDragging } = useDraggable({
    id: `quote-item-${itemKey}`,
    data: { sourceIndex: index },
  });
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `quote-row-${itemKey}`,
    data: { kind: "before", stage, subGroup: subGroup ?? null, targetIndex: index } satisfies QuotePlacementDropData,
  });
  const setNodeRef = (node: HTMLDivElement | null) => {
    setDraggableRef(node);
    setDroppableRef(node);
  };
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform) }}
      className={`flex items-center gap-1 ${isDragging ? "opacity-80 z-50" : ""} ${isOver && !isDragging ? "ring-2 ring-primary/70 rounded-md" : ""}`}
      data-placement-dragging={isDragging ? "true" : undefined}
      data-testid={`draggable-quote-item-${index}`}
    >
      <div className="flex items-center justify-center shrink-0">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="touch-none p-1 text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing"
          title="Drag to move this item within the quote"
          aria-label="Drag to move this work item within the quote"
          data-testid={`drag-handle-item-${index}`}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

export function QuotePlacementDropTarget({
  id,
  data,
  children,
}: {
  id: string;
  data: QuotePlacementDropData;
  children?: ReactNode;
}) {
  const { isOver, setNodeRef } = useDroppable({ id, data });
  if (children) {
    return (
      <div
        ref={setNodeRef}
        className={isOver ? "ring-2 ring-primary ring-inset rounded-md" : ""}
        data-testid={`drop-target-${id}`}
      >
        {children}
      </div>
    );
  }
  return (
    <div
      ref={setNodeRef}
      className={`h-0 rounded transition-all ${
        isOver
          ? "ring-2 ring-primary"
          : ""
      }`}
      aria-label={`Drop item in ${data.subGroup || data.stage}`}
      data-testid={`drop-target-${id}`}
    />
  );
}